// Task 32 — Spatial index coach: pure `advise()` scoring + the `hasSpatialIndex` anti-nag probe.
// The per-engine catalog SQL lives in the sibling `spatialIndexProbeQueries.ts` and
// `buildSpatialIndexScript` lives in `spatialIndexScripts.ts` — both split out to stay under the
// repo's ~200-line-per-file guideline.

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SpatialPlanScanNode } from './spatialPlanExtract';
import { probeSpatialIndex } from './spatialIndexProbeQueries';

export const SPATIAL_INDEX_COACH_ROW_THRESHOLD = 10_000;

/** Full-table-scan node types per engine (spec §15 / Task 32) — the ONLY node shapes `advise()` fires on. */
export const FULL_SCAN_NODE_TYPES: Partial<Record<DatabaseEngineId, readonly string[]>> = {
  pgsql: ['Seq Scan'],
  mysql: ['ALL'],
  mssql: ['Table Scan', 'Clustered Index Scan'],
  oracle: ['TABLE ACCESS FULL'],
};

export type SpatialIndexAdvice = { readonly show: boolean; readonly reason: string };

function bareTableKey(value: string): string {
  return value
    .replace(/["`[\]]/g, '')
    .replace(/^.*\./, '')
    .trim()
    .toLowerCase();
}

/**
 * Scores a derived scan-node list against a known spatial filter. Shows `true` when the filtered
 * table appears under a full-scan node (per `FULL_SCAN_NODE_TYPES`) with `rows >= 10_000`. Never
 * touches raw plan XML/JSON — `plan.nodes` must already be the `extractScanNodes` output.
 */
export function advise(
  plan: { nodes: readonly SpatialPlanScanNode[] },
  spatialFilter: { table: string; column: string },
  engine: DatabaseEngineId
): SpatialIndexAdvice {
  const fullScanTypes = FULL_SCAN_NODE_TYPES[engine];
  const targetTable = spatialFilter.table.trim();
  if (!fullScanTypes || targetTable.length === 0) {
    return { show: false, reason: 'This engine is not covered by the spatial index coach.' };
  }

  const targetKey = bareTableKey(targetTable);
  let worstOffender: SpatialPlanScanNode | null = null;
  for (const node of plan.nodes) {
    if (!node.relation || bareTableKey(node.relation) !== targetKey) continue;
    if (!fullScanTypes.includes(node.nodeType)) continue;
    if (!worstOffender || node.rows > worstOffender.rows) worstOffender = node;
  }

  if (!worstOffender || worstOffender.rows < SPATIAL_INDEX_COACH_ROW_THRESHOLD) {
    return {
      show: false,
      reason: `No full scan of at least ${SPATIAL_INDEX_COACH_ROW_THRESHOLD.toLocaleString()} rows detected on "${targetTable}".`,
    };
  }

  return {
    show: true,
    reason: `${worstOffender.nodeType} scanned ~${Math.round(worstOffender.rows).toLocaleString()} rows on "${targetTable}" — a spatial index on "${spatialFilter.column}" could speed up this filter.`,
  };
}

// ---------------------------------------------------------------------------
// hasSpatialIndex — anti-nag guard (TE trigger). One cheap catalog query per
// engine, memoized per (connection, table, column) — positive AND negative,
// so a repeat call never re-queries. Any failure (permission, missing
// catalog view, connection hiccup) → `null`, meaning "unknown" — callers
// soften the hint's wording rather than suppressing it.
// ---------------------------------------------------------------------------

const indexCache = new Map<string, boolean | null>();

function cacheKey(connectionId: string, table: string, column: string): string {
  return `${connectionId}::${table.toLowerCase()}::${column.toLowerCase()}`;
}

/**
 * Whether `schema.table.column` already has a spatial index, per engine. Memoized (positive AND
 * negative) per (connection, table, column) for the session — repeated calls from render churn
 * (or from multiple surfaces checking the same column) hit the cache instead of re-querying.
 * `null` = the probe failed/errored — callers must treat that as "unknown", not "no index".
 *
 * Pass `forceRefresh: true` when the caller knows the underlying data was genuinely refetched
 * (e.g. Table Explorer just loaded a new window) — otherwise a user who creates the suggested
 * index via a different surface (Query Editor) and reloads would see a stale "no index" verdict
 * forever, since the cache has no other way to learn the index now exists.
 */
export async function hasSpatialIndex(
  engine: DatabaseEngineId,
  connectionId: string,
  database: string,
  schema: string,
  table: string,
  column: string,
  options?: { forceRefresh?: boolean }
): Promise<boolean | null> {
  if (!connectionId) return null;
  const key = cacheKey(connectionId, table, column);
  if (!options?.forceRefresh) {
    const cached = indexCache.get(key);
    if (cached !== undefined) return cached;
  }

  const result = await probeSpatialIndex(engine, connectionId, database, schema, table, column);
  indexCache.set(key, result);
  return result;
}

/** Test-only: clears the memoization cache between spec runs. */
export function __resetSpatialIndexCacheForTests(): void {
  indexCache.clear();
}
